import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  PaginatedRouteOptions,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import { z } from 'zod';
import {
  AccountAttributes,
  AddAccountBody,
  AddDomainInput,
  AddProgramInput,
  CurrencyAttributes,
  DomainAttributes,
  GetDomainsInput,
  ProgramAttributes,
  ProgramStatus,
  ReferralConfigAttributes,
  ReferralConfigI18n,
  SetSsoConfigBody,
  SetSsoConfigQuery,
  SsoConfigAttributes,
  UpdateAccountBody,
  UpdateProgramInput,
} from '.';
import { AddLevelBody, LevelAttributes, UpdateLevelBody } from './types/level';
import { ApplyImpactAccountData } from '../profile-api-client';
import { LevelsConfigAttributes, SetLevelConfigBody } from './types/level-config';

// TODO: find a better place to declare this constant ?
export const oauth2ProxyCallbackPath = '/oauth2-proxy/callback';

export const $GetProgramRelations = z.object({
  withLevels: z.coerce.boolean().default(false).optional(),
  withLevelConfig: z.coerce.boolean().default(false).optional(),
  withCurrencies: z.coerce.boolean().default(false).optional(),
  withDomains: z.coerce.boolean().default(false).optional(),
  withReferralConfig: z.coerce.boolean().default(false).optional(),
  withSsoConfig: z.coerce.boolean().default(false).optional(),
});

export type GetProgramRelations = z.infer<typeof $GetProgramRelations>;

export const $GetProgramOptions = $GetProgramRelations.extend({
  withDeleted: z.coerce.boolean().default(false).optional(),
});

export type GetProgramOptions = z.infer<typeof $GetProgramOptions>;

export const $GetProgramByDomainQuery = $GetProgramRelations.extend({
  host: z.string(),
});

export type GetProgramByDomainQuery = z.infer<typeof $GetProgramByDomainQuery>;

export enum DomainSetupSteps {
  DATABASE = 'DATABASE',
  TEXTFIELD_VERIFICATION = 'TEXTFIELD_VERIFICATION',
  CNAME_VERIFICATION = 'CNAME_VERIFICATION',
  SSL_CERTIFICATE = 'SSL_CERTIFICATE',
  UPDATE_ROUTING = 'UPDATE_ROUTING',
  WHITELIST_REDIRECT_URI = 'WHITELIST_REDIRECT_URI',
  PARTIALLY_DELETED = 'PARTIALLY_DELETED',
}

export interface ReferralConfigInput {
  /**
   * @deprecated
   */
  link?: string | null;
  i18nAttributes: ReferralConfigI18n;
}

export const $GetAccountDataQueryParams = z.object({ programId: z.string().uuid().optional() });

export type GetAccountDataQueryParams = z.infer<typeof $GetAccountDataQueryParams>;

export class GamificationApiClient extends ApiClient {
  static serviceName = 'gamification-api';

  constructor(http?: AxiosInstance, context?: ApiClientContext, options?: ApiClientOptions) {
    super('/api/gamification', http, context, options);
  }

  async getAccount(accountId: string, options?: RouteOptions): Promise<AccountAttributes> {
    const { data } = await this.http.get(`/accounts/${accountId}`, defaultAxiosConfig(options));
    return data;
  }

  async getAccounts(options?: PaginatedRouteOptions): Promise<AccountAttributes[]> {
    const { data } = await this.http.get('/accounts', {
      ...defaultAxiosConfig(options),
      params: {
        ...options?.params,
        ...options?.pagination,
      },
    });
    return data;
  }

  async getAllAccounts(options?: RouteOptions): Promise<AccountAttributes[]> {
    let pageNumber = 0;
    let accountsToAdd: AccountAttributes[] = [];
    const accumulatedAccounts: AccountAttributes[] = [];
    do {
      accountsToAdd = await this.getAccounts({
        ...options,
        pagination: { pageSize: 100, pageNumber },
      });
      accumulatedAccounts.push(...accountsToAdd);
      pageNumber++;
    } while (accountsToAdd.length === 100);
    return accumulatedAccounts;
  }

  async addAccount(account: AddAccountBody, options?: RouteOptions): Promise<AccountAttributes> {
    const { data } = await this.http.post(`/accounts`, account, defaultAxiosConfig(options));
    return data;
  }

  async archiveAccount(accountId: string, options?: RouteOptions): Promise<AccountAttributes> {
    const { data } = await this.http.delete(`/accounts/${accountId}`, defaultAxiosConfig(options));
    return data;
  }

  async restoreAccount(accountId: string, options?: RouteOptions): Promise<AccountAttributes> {
    const { data } = await this.http.put(`/accounts/${accountId}`, defaultAxiosConfig(options));
    return data;
  }

  async updateAccount(accountId: string, body: UpdateAccountBody, options?: RouteOptions) {
    const { data } = await this.http.patch(
      `/accounts/${accountId}`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async addProgram(
    accountId: string,
    program: AddProgramInput,
    options?: RouteOptions,
  ): Promise<ProgramAttributes> {
    const { data } = await this.http.post(
      `/accounts/${accountId}/programs`,
      program,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async deleteProgram(id: string, options?: RouteOptions): Promise<ProgramAttributes> {
    const { data } = await this.http.delete(`/programs/${id}`, defaultAxiosConfig(options));
    return data;
  }

  async getProgram(
    programId: string,
    options?: RouteOptions<GetProgramRelations>,
  ): Promise<ProgramAttributes> {
    const { data } = await this.http.get(`/programs/${programId}`, defaultAxiosConfig(options));
    return data;
  }

  async getPrograms(
    accountId: string,
    options?: RouteOptions<GetProgramOptions>,
  ): Promise<ProgramAttributes[]> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/programs`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async updateProgram(
    id: string,
    program: UpdateProgramInput,
    options?: RouteOptions,
  ): Promise<ProgramAttributes> {
    const { data } = await this.http.patch(`/programs/${id}`, program, defaultAxiosConfig(options));
    return data;
  }

  async markProgramForRefresh(programId: string, options?: RouteOptions): Promise<void> {
    await this.http.post(`/programs/${programId}/changed`, {}, defaultAxiosConfig(options));
  }

  async addCurrency(
    programId: string,
    attributes: Omit<CurrencyAttributes, 'state'>,
    options?: RouteOptions,
  ): Promise<CurrencyAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/currencies`,
      attributes,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getCurrencies(programId: string, options?: RouteOptions): Promise<CurrencyAttributes[]> {
    const { data } = await this.http.get(
      `/programs/${programId}/currencies`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getCurrenciesByIds(ids: string[], options?: RouteOptions): Promise<CurrencyAttributes[]> {
    const { data } = await this.http.get('/currencies/by-ids', {
      ...defaultAxiosConfig(options),
      params: {
        ...options?.params,
        ids,
      },
    });
    return data;
  }

  async updateCurrency(
    id: string,
    attributes: CurrencyAttributes,
    options?: RouteOptions,
  ): Promise<CurrencyAttributes> {
    const { data } = await this.http.patch(
      `/currencies/${id}`,
      attributes,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async archiveCurrency(id: string, options?: RouteOptions): Promise<void> {
    await this.http.delete(`/currencies/${id}`, defaultAxiosConfig(options));
  }

  async restoreCurrency(id: string, options?: RouteOptions): Promise<void> {
    await this.http.patch(`/currencies/${id}/restore`, {}, defaultAxiosConfig(options));
  }

  async getAccountCurrencies(
    accountId: string,
    options?: RouteOptions,
  ): Promise<{ [programId: string]: CurrencyAttributes[] }> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/currencies`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getLevelConfig(
    programId: string,
    options?: RouteOptions,
  ): Promise<LevelsConfigAttributes | null> {
    const { data } = await this.http.get(
      `/programs/${programId}/levels-config`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async setLevelConfig(
    programId: string,
    body: SetLevelConfigBody,
    options?: RouteOptions,
  ): Promise<LevelsConfigAttributes> {
    const { data } = await this.http.put(
      `/programs/${programId}/levels-config`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getAccountLevelsConfigs(
    accountId: string,
    options?: RouteOptions,
  ): Promise<{ [programId: string]: LevelsConfigAttributes }> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/levels-configs`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getLevels(programId: string, options?: RouteOptions): Promise<LevelAttributes[]> {
    const { data } = await this.http.get(
      `/programs/${programId}/levels`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async addLevel(
    programId: string,
    body: AddLevelBody,
    options?: RouteOptions,
  ): Promise<LevelAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/levels`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getLevel(levelId: string, options?: RouteOptions): Promise<LevelAttributes> {
    const { data } = await this.http.get(`/levels/${levelId}`, defaultAxiosConfig(options));
    return data;
  }

  async updateLevel(body: UpdateLevelBody, options?: RouteOptions): Promise<LevelAttributes> {
    const { data } = await this.http.put(`/levels/${body.id}`, body, defaultAxiosConfig(options));
    return data;
  }

  async deleteLevel(levelId: string, options?: RouteOptions<{ soft?: boolean }>): Promise<void> {
    await this.http.delete(`/levels/${levelId}`, defaultAxiosConfig(options));
  }

  async getAccountLevels(
    accountId: string,
    options?: RouteOptions,
  ): Promise<{ [programId: string]: LevelAttributes[] }> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/levels`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async upsertReferralConfig(
    programId: string,
    referralConfig: ReferralConfigInput,
    options?: RouteOptions,
  ): Promise<ReferralConfigAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/referral-config`,
      referralConfig,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getReferralConfig(
    programId: string,
    options?: RouteOptions,
  ): Promise<ReferralConfigAttributes | null> {
    const { data } = await this.http.get(
      `/programs/${programId}/referral-config`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async addSubdomain(
    accountId: string,
    addDomainInput: AddDomainInput,
    options?: RouteOptions,
  ): Promise<DomainAttributes> {
    const { data } = await this.http.post(
      `/accounts/${accountId}/domains`,
      addDomainInput,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getSubdomains(
    getdomainInputs: GetDomainsInput,
    options?: RouteOptions,
  ): Promise<DomainAttributes[]> {
    const { data } = await this.http.get(`/accounts/${getdomainInputs.accountId}/domains`, {
      ...defaultAxiosConfig(options),
      params: {
        ...options?.params,
        ...(getdomainInputs.programId ? { programId: getdomainInputs.programId } : {}),
        ...(getdomainInputs.id ? { id: getdomainInputs.id } : {}),
      },
    });
    return data;
  }

  async deleteSubdomain(domainId: string, options?: RouteOptions): Promise<DomainAttributes> {
    const { data } = await this.http.delete(`/domains/${domainId}`, defaultAxiosConfig(options));
    return data;
  }

  async updateSubdomain(
    domain: DomainAttributes,
    options?: RouteOptions,
  ): Promise<DomainAttributes> {
    const { data } = await this.http.patch(
      `/domains/${domain.id}`,
      domain,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getSubdomain(domainId: string, options?: RouteOptions): Promise<DomainAttributes> {
    const { data } = await this.http.get(`/domains/${domainId}`, defaultAxiosConfig(options));
    return data;
  }

  async getProgramByDomain(
    host: string,
    options?: RouteOptions<GetProgramByDomainQuery>,
  ): Promise<ProgramAttributes> {
    const { data } = await this.http.get('/programs/domain/get', {
      ...defaultAxiosConfig(options),
      params: { ...options?.params, host } satisfies GetProgramByDomainQuery,
    });

    return data;
  }

  async verifySubdomainDuplication(
    accountId: string,
    hostname: string,
    options?: RouteOptions,
  ): Promise<boolean> {
    const { data } = await this.http.get(`/accounts/${accountId}/domain-duplication`, {
      ...defaultAxiosConfig(options),
      params: { ...options?.params, hostname },
    });
    return data.duplicated;
  }

  async getProgramSubdomains(
    programId: string,
    options?: RouteOptions,
  ): Promise<DomainAttributes[]> {
    const { data } = await this.http.get(
      `/programs/${programId}/domains`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async setProgramStatus(
    programId: string,
    status: ProgramStatus,
    options?: RouteOptions,
  ): Promise<void> {
    await this.http.patch(
      `/programs/${programId}/status/${status}`,
      {},
      defaultAxiosConfig(options),
    );
  }

  async publishTemplate(
    programId: string,
    templateId: string,
    options?: RouteOptions,
  ): Promise<ProgramAttributes> {
    const { data } = await this.http.patch(
      `/programs/${programId}/template/${templateId}`,
      {},
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getAccountData(
    accountId: string,
    options?: RouteOptions<GetAccountDataQueryParams>,
  ): Promise<ApplyImpactAccountData> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/data`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  /** @deprecated */
  async getSsoConfigsPrivileged(options?: RouteOptions): Promise<SsoConfigAttributes[]> {
    const { data } = await this.http.get(`/sso-configs/privileged`, defaultAxiosConfig(options));
    return data;
  }

  async setSsoConfig(
    accountId: string,
    body: SetSsoConfigBody,
    options?: RouteOptions<SetSsoConfigQuery>,
  ): Promise<void> {
    await this.http.put(`/accounts/${accountId}/sso-configs`, body, defaultAxiosConfig(options));
  }
}
